import { useEffect, useMemo, useState, useCallback } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { AsyncState } from "@/components/common/Loader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Mail, Phone, Trash2, Search, Download, ChevronDown, User, StickyNote, MapPin, Target, Trophy, Clock } from "lucide-react";

const STATUSES = ["New", "Contacted", "Qualified", "Won", "Lost"];
const SOURCES = ["google", "instagram", "facebook", "direct", "ad", "referral", "event", "unknown"];
const statusColor = {
  New: "text-[hsl(199_89%_55%)]",
  Contacted: "text-[hsl(48_96%_55%)]",
  Qualified: "text-[hsl(280_70%_65%)]",
  Won: "text-[hsl(142_60%_50%)]",
  Lost: "text-muted-foreground",
};
const sourceLabel = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "Direct");

function toCsv(rows) {
  const headers = ["Created", "Name", "Email", "Phone", "Status", "Source", "Athlete", "Age/Grade", "Sport", "Goal", "Location", "Interested In", "Best Time", "Owner", "Message"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((l) => {
    lines.push([
      l.created_at ? new Date(l.created_at).toLocaleString() : "",
      l.name, l.email, l.phone, l.status || "New", l.source || "direct",
      l.athlete_name, l.age, l.sport || l.sport_goal, l.goal, l.preferred_location,
      l.interested_in, l.best_time, l.assigned_owner, l.message,
    ].map(esc).join(","));
  });
  return lines.join("\n");
}

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});
  const [ownerDraft, setOwnerDraft] = useState({});

  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("All");
  const [fSource, setFSource] = useState("All");
  const [fLocation, setFLocation] = useState("All");

  const load = useCallback(() => { setLoading(true); api.get("/admin/leads").then((r) => setLeads(r.data)).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  const locations = useMemo(() => Array.from(new Set(leads.map((l) => l.preferred_location).filter(Boolean))), [leads]);

  const update = async (id, payload, msg = "Updated") => {
    try { await api.put(`/admin/leads/${id}`, payload); toast.success(msg); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/leads/${id}`); toast.success("Lead deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const addNote = async (id) => {
    const note = (noteDraft[id] || "").trim();
    if (!note) return;
    await update(id, { internal_note: note }, "Note added");
    setNoteDraft((d) => ({ ...d, [id]: "" }));
  };

  const filtered = useMemo(() => {
    return leads.filter((l) => {
      if (fStatus !== "All" && (l.status || "New") !== fStatus) return false;
      if (fSource !== "All" && (l.source || "direct") !== fSource) return false;
      if (fLocation !== "All" && l.preferred_location !== fLocation) return false;
      if (q) {
        const hay = `${l.name} ${l.email} ${l.phone} ${l.athlete_name} ${l.sport} ${l.goal} ${l.sport_goal} ${l.interested_in} ${l.message}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [leads, fStatus, fSource, fLocation, q]);

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("No leads to export"); return; }
    const blob = new Blob([toCsv(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trilogy-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} lead(s)`);
  };

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display-title text-4xl">Leads &amp; Inquiries</h1>
          <p className="mt-1 text-sm text-muted-foreground">{filtered.length} of {leads.length} inquiry(ies)</p>
        </div>
        <button onClick={exportCsv} data-testid="leads-export-csv" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Download className="h-4 w-4 text-brand" /> Export CSV</button>
      </div>

      {/* Filters */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads" data-testid="lead-search" className="pl-9" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger data-testid="lead-filter-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="All">All Statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fSource} onValueChange={setFSource}><SelectTrigger data-testid="lead-filter-source"><SelectValue placeholder="Source" /></SelectTrigger><SelectContent><SelectItem value="All">All Sources</SelectItem>{SOURCES.map((s) => <SelectItem key={s} value={s}>{sourceLabel(s)}</SelectItem>)}</SelectContent></Select>
        <Select value={fLocation} onValueChange={setFLocation}><SelectTrigger data-testid="lead-filter-location"><SelectValue placeholder="Location" /></SelectTrigger><SelectContent><SelectItem value="All">All Locations</SelectItem>{locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
      </div>

      <AsyncState loading={loading} empty={filtered.length === 0} emptyTitle="No leads" emptyDescription="No leads match your filters.">
        <div className="mt-6 space-y-4">
          {filtered.map((l) => {
            const open = expanded === l.id;
            const status = l.status || "New";
            return (
              <div key={l.id} className="rounded-xl border border-border bg-card p-5" data-testid="admin-lead-card">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={cn("text-xs font-semibold uppercase tracking-wide", statusColor[status] || statusColor.New)}>{status}</span>
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase text-muted-foreground">{sourceLabel(l.source)}</span>
                      {l.assigned_owner && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><User className="h-3 w-3" />{l.assigned_owner}</span>}
                    </div>
                    <h3 className="mt-1 font-condensed text-lg font-semibold">{l.name}</h3>
                    <div className="mt-1 flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-brand" />{l.email}</span>
                      {l.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-brand" />{l.phone}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={status} onValueChange={(v) => update(l.id, { status: v })}>
                      <SelectTrigger className="w-36" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <button onClick={() => setExpanded(open ? null : l.id)} data-testid={`lead-expand-${l.id}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-accent transition-colors"><ChevronDown className={cn("h-4 w-4 transition-transform", open && "rotate-180")} /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="flex h-9 w-9 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10 transition-colors" data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this lead?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(l.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Quick facts */}
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {l.athlete_name && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" />{l.athlete_name}{l.age ? ` (${l.age})` : ""}</span>}
                  {(l.sport || l.sport_goal) && <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" />{l.sport || l.sport_goal}</span>}
                  {l.goal && <span className="inline-flex items-center gap-1"><Target className="h-3 w-3" />{l.goal}</span>}
                  {l.preferred_location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{l.preferred_location}</span>}
                  {l.best_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{l.best_time}</span>}
                  <span>{l.created_at ? new Date(l.created_at).toLocaleString() : ""}</span>
                </div>

                {l.message && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{l.message}</p>}

                {open && (
                  <div className="mt-4 space-y-4 border-t border-border pt-4">
                    {l.interested_in && <p className="text-sm text-muted-foreground">Interested in: <span className="text-foreground/90">{l.interested_in}</span></p>}
                    {/* Assign owner + contact */}
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex-1 min-w-[200px]">
                        <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assign Owner</label>
                        <div className="mt-1 flex gap-2">
                          <Input value={ownerDraft[l.id] ?? (l.assigned_owner || "")} onChange={(e) => setOwnerDraft((d) => ({ ...d, [l.id]: e.target.value }))} placeholder="Owner name or email" data-testid={`lead-owner-${l.id}`} />
                          <button onClick={() => update(l.id, { assigned_owner: ownerDraft[l.id] ?? (l.assigned_owner || "") }, "Owner assigned")} data-testid={`lead-assign-${l.id}`} className="rounded-md bg-brand px-4 text-sm font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors">Assign</button>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a href={`mailto:${l.email}?subject=${encodeURIComponent("Re: Your Trilogy inquiry")}`} data-testid={`lead-email-${l.id}`} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Mail className="h-4 w-4 text-brand" /> Email</a>
                        {l.phone && <a href={`tel:${l.phone.replace(/[^0-9+]/g, "")}`} className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold uppercase tracking-wide hover:bg-accent transition-colors"><Phone className="h-4 w-4 text-brand" /> Call</a>}
                      </div>
                    </div>

                    {/* Internal notes */}
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><StickyNote className="h-3.5 w-3.5" /> Internal Notes</p>
                      {Array.isArray(l.internal_notes) && l.internal_notes.length > 0 && (
                        <ul className="mt-2 space-y-2">
                          {l.internal_notes.map((n) => (
                            <li key={n.at || n.note} className="rounded-md bg-muted/40 p-3 text-sm">
                              <p className="text-foreground/90">{n.note}</p>
                              <p className="mt-1 text-[11px] text-muted-foreground">{n.author} · {n.at ? new Date(n.at).toLocaleString() : ""}</p>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-2 flex gap-2">
                        <Textarea rows={2} value={noteDraft[l.id] || ""} onChange={(e) => setNoteDraft((d) => ({ ...d, [l.id]: e.target.value }))} placeholder="Add an internal note (staff only)" data-testid={`lead-note-input-${l.id}`} />
                        <button onClick={() => addNote(l.id)} data-testid={`lead-note-add-${l.id}`} className="shrink-0 rounded-md bg-brand px-4 text-sm font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] transition-colors">Add</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </AsyncState>
    </AdminLayout>
  );
}
